import { Box, Divider, Stack, Typography, useTheme } from "@mui/material";
import {
  Button,
  CircularLoader,
  DataTable,
  SelectField,
  Tab,
  Tabs,
} from "components";
import {
  useGetAllCustomerUsers,
  useGetNinjaMappedCustomers,
  useGetNinjaMappedDevices,
  useGetNinjaOneDevices,
  useMapNinjaOneDevices,
} from "controller";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import toast from "react-hot-toast";
import { emptyStringHandler, getFullName } from "utilz/helpers";
import { useDebounce } from "utilz/helpers/hooks/useDebounceHook";

type DeviceMapping = {
  device_id: string;
  device_name: string;
  deskday_customer_user_id: string;
  deskday_customer_user_name: string;
  remote_control_uri: string;
  meta_data: Record<string, unknown>;
};

type NinjaMappedCustomer = {
  mapping_id: string;
  external_customer_id: string | number;
  external_customer_name: string;
  deskday_customer_id: string;
  [key: string]: unknown;
};

type NinjaMappedDevice = {
  device_id: string | number;
  deskday_customer_user_id: string;
  deskday_customer_user_name: string;
  [key: string]: unknown;
};

type CustomerUser = {
  mapping_id: string;
  first_name?: string;
  middle_name?: string;
  last_name?: string;
  [key: string]: unknown;
};

type UnmappedDevice = {
  device_id: string;
  deskday_customer_user_id: string;
};

type SubmitItem = {
  mapping_id: string;
  external_customer_id: number;
  deskday_customer_id: string;
  devices: DeviceMapping[];
  unmapped: UnmappedDevice[];
};

const DEVICE_PAGE_SIZE = 100;

export const NinjaOneStep2 = forwardRef((_props, ref) => {
  const theme = useTheme();
  const [selectedCustomer, setSelectedCustomer] = useState(0);

  // Saved customer mappings from Step 1 (drives the tabs)
  const { data: ninjaMappedCustomers, isLoading: isMappedCustomersLoading } =
    useGetNinjaMappedCustomers("ninjaOne");
  // Already-saved device→user mappings (used to pre-fill rows)
  const { data: ninjaMappedDevices, isLoading: isMappedDevicesLoading } =
    useGetNinjaMappedDevices("ninjaOne");
  const { mutateAsync: mapDevice } = useMapNinjaOneDevices();

  // The mapped customer whose tab is currently active
  const currentMappedCustomer = ninjaMappedCustomers?.[selectedCustomer];
  const selectedOrgId = currentMappedCustomer
    ? Number(currentMappedCustomer.external_customer_id)
    : undefined;
  const selectedDeskdayCustomerId = currentMappedCustomer?.deskday_customer_id;

  // Per-tab paginated devices (only fires when an org is selected)
  const {
    data: ninjaDevicesData,
    isLoading: isNinjaDeviceLoading,
    isFetching: isNinjaDeviceFetching,
    fetchNextPage: fetchNinjaDevicesNextPage,
    hasNextPage: hasNinjaDevicesNextPage,
    isFetchingNextPage: isFetchingNinjaDevicesNextPage,
  } = useGetNinjaOneDevices(selectedOrgId, DEVICE_PAGE_SIZE);

  // Holds in-progress + loaded device mappings keyed by NinjaOne org id.
  // Survives tab switches so user edits aren't lost.
  const [mappingsByOrg, setMappingsByOrg] = useState<
    Record<number, DeviceMapping[]>
  >({});

  // Debounced server-side search — keeps users beyond the first page
  // reachable instead of filtering only the loaded page.
  const [userSearch, setUserSearch] = useDebounce("", 400);
  const { data: customerUsers, isFetching: isCustomerUsersFetching } =
    useGetAllCustomerUsers({
      perPageCount: 2000,
      sortColumn: "first_name",
      sortDirection: "asc",
      ...(selectedDeskdayCustomerId && {
        customerId: selectedDeskdayCustomerId,
      }),
      filter: [
        { column: "customer_users.status", operator: "=", value: "true" },
      ],
      searchQuery: userSearch,
    });

  // When devices for the active org load (or saved mappings change),
  // merge fetched devices + saved mappings + existing edits.
  useEffect(() => {
    // Wait for the saved mappings too: rows already built are kept as-is, so
    // building before they land would never pre-fill the saved users.
    if (!selectedOrgId || !ninjaDevicesData?.pages || !ninjaMappedDevices)
      return;

    const fetched = ninjaDevicesData.pages.flatMap((page) => page?.data || []);

    setMappingsByOrg((prev) => {
      const existing = prev[selectedOrgId] || [];
      const existingById = new Map(
        existing.map((d) => [String(d.device_id), d]),
      );

      const merged: DeviceMapping[] = fetched.map((obj) => {
        const idStr = String(obj.id);
        // Preserve in-progress edits if we already have an entry
        if (existingById.has(idStr)) {
          return existingById.get(idStr) as DeviceMapping;
        }
        // Build fresh entry, pre-filling from saved mappings
        const savedIdx =
          (ninjaMappedDevices as NinjaMappedDevice[] | undefined)?.findIndex(
            (device) => String(device.device_id) === idStr,
          ) ?? -1;
        const hasSaved = savedIdx > -1;
        const savedDevice = hasSaved
          ? (ninjaMappedDevices as NinjaMappedDevice[])[savedIdx]
          : undefined;
        return {
          device_id: String(obj.id),
          device_name: obj.systemName ?? "",
          deskday_customer_user_id: savedDevice?.deskday_customer_user_id ?? "",
          deskday_customer_user_name:
            savedDevice?.deskday_customer_user_name ?? "",
          remote_control_uri: obj.remoteControlUri || "",
          meta_data: obj,
        };
      });

      return { ...prev, [selectedOrgId]: merged };
    });
  }, [ninjaDevicesData, selectedOrgId, ninjaMappedDevices]);

  const currentDevices = useMemo(
    () => (selectedOrgId ? mappingsByOrg[selectedOrgId] || [] : []),
    [mappingsByOrg, selectedOrgId],
  );

  // Saved mappings still loading, or devices in but rows not yet built (the
  // effect above runs a render after the queries settle). Keep the skeleton
  // up, not "No devices found", until the rows exist.
  const isBuildingRows =
    isMappedDevicesLoading ||
    (Boolean(ninjaDevicesData?.pages?.some((page) => page?.data?.length)) &&
      currentDevices.length === 0);

  const handleUserChange = ({
    id,
    deviceId,
  }: {
    id: string;
    deviceId: string;
  }) => {
    if (!selectedOrgId || !deviceId) return;

    const users = (customerUsers?.result ?? []) as CustomerUser[];
    const matchedUser = id
      ? users.find((user) => user.mapping_id === id)
      : undefined;
    if (id && !matchedUser) return;

    const fullname = matchedUser
      ? getFullName(
          matchedUser.first_name,
          matchedUser.middle_name,
          matchedUser.last_name,
        )
      : "";

    setMappingsByOrg((prev) => {
      const list = prev[selectedOrgId] || [];
      const idx = list.findIndex((d) => d.device_id === deviceId);
      if (idx === -1) return prev;
      const next = [...list];
      next[idx] = {
        ...next[idx],
        deskday_customer_user_id: matchedUser?.mapping_id ?? "",
        deskday_customer_user_name: fullname,
      };
      return { ...prev, [selectedOrgId]: next };
    });
  };

  const handleTransformForSubmit = () => {
    const submitArray: Array<SubmitItem> = [];

    // Initial saved state: device_id -> previously mapped user id.
    // Used as the "before" snapshot to diff the user's edits against.
    const initialUserByDevice = new Map<string, string>();
    (ninjaMappedDevices as NinjaMappedDevice[] | undefined)?.forEach((d) => {
      if (d.deskday_customer_user_id) {
        initialUserByDevice.set(
          String(d.device_id),
          d.deskday_customer_user_id,
        );
      }
    });

    // Delta payload — only send what changed. Backend upserts `devices` and
    // deletes `unmapped`; anything not sent is left untouched (no pagination
    // data loss). Unvisited org tabs are skipped entirely.
    (ninjaMappedCustomers as NinjaMappedCustomer[] | undefined)?.forEach(
      (customer) => {
        const orgId = Number(customer.external_customer_id);
        const orgDevices = mappingsByOrg[orgId];
        if (!orgDevices) return; // never visited — nothing changed

        const devices: DeviceMapping[] = [];
        const unmapped: UnmappedDevice[] = [];

        orgDevices.forEach((d) => {
          const initialUserId = initialUserByDevice.get(d.device_id) ?? "";
          const currentUserId = d.deskday_customer_user_id ?? "";

          if (currentUserId === initialUserId) return; // unchanged

          if (currentUserId) {
            // newly mapped or changed to a different user → upsert
            devices.push(d);
          } else {
            // had a mapping, now cleared → remove. Backend only needs the
            // device id + the user mapping being removed.
            unmapped.push({
              device_id: d.device_id,
              deskday_customer_user_id: initialUserId,
            });
          }
        });

        if (devices.length === 0 && unmapped.length === 0) return; // no change

        submitArray.push({
          mapping_id: customer.mapping_id,
          external_customer_id: orgId,
          deskday_customer_id: customer.deskday_customer_id,
          devices,
          unmapped,
        });
      },
    );
    return submitArray;
  };

  const cellBuilder = {
    user_name: (cellData: DeviceMapping) => {
      return (
        <SelectField
          key={cellData.device_id}
          sx={{ width: "180px" }}
          name="customer"
          hideHelperBox
          options={customerUsers?.result || []}
          isSearchable
          isLoading={isCustomerUsersFetching}
          onSearchValueChange={setUserSearch}
          // Search is served by the backend (`searchQuery`), so `onFilterValue`
          // returns true to stop the client from re-filtering the results.
          onFilterValue={() => true}
          value={cellData.deskday_customer_user_id}
          size="small"
          isResponsive={false}
          labelMappingKey="first_name"
          valueMappingKey="mapping_id"
          searchFieldProps={{
            placeholder: "Search user",
          }}
          menuItemBuilder={({ item, index }) => (
            <Box key={index}>
              {emptyStringHandler(item.first_name)}{" "}
              {emptyStringHandler(item.last_name)}
            </Box>
          )}
          onValueChange={(value) =>
            handleUserChange({ id: value, deviceId: cellData.device_id })
          }
          isClearable
          // The options list shrinks while a search is active, so fall back to
          // the name held in row state to keep every row's label stable.
          renderValue={(_selected, selectedOption) => {
            return selectedOption ? (
              <>
                {emptyStringHandler(selectedOption?.first_name)}{" "}
                {emptyStringHandler(selectedOption?.last_name)}
              </>
            ) : (
              emptyStringHandler(cellData.deskday_customer_user_name)
            );
          }}
        />
      );
    },
  };

  const onSubmit = async () => {
    const data = handleTransformForSubmit();
    if (data.length > 0) {
      try {
        await mapDevice({ deviceMapping: data, source: "ninjaOne" });
        toast.success("Successfully mapped devices");
        return true;
      } catch {
        toast.error("Failed to map devices");
        return false;
      }
    } else {
      return true;
    }
  };

  useImperativeHandle(ref, () => ({
    onSubmit,
  }));

  return (
    <Stack
      sx={{
        py: "24px",
        height: "calc(100% - 34px)",
        overflowY: "scroll",
      }}
      divider={
        <Divider
          flexItem
          sx={{
            marginTop: "24px",
          }}
        />
      }
    >
      <Stack sx={{ px: "32px", gap: "16px" }}>
        <Typography variant="h6">Device mapping</Typography>
        <Typography variant="body-medium" color={theme.palette.lightBg.low}>
          Please map your users with corresponding device names in NinjaOne RMM
        </Typography>
      </Stack>

      {isMappedCustomersLoading ? (
        <Stack flex={1} alignItems="center" justifyContent="center">
          <CircularLoader size={32} />
        </Stack>
      ) : !ninjaMappedCustomers?.length ? (
        <Stack
          flex={1}
          alignItems="center"
          justifyContent="center"
          spacing={1}
          sx={{ px: "32px", textAlign: "center" }}
        >
          <Typography variant="h6">No customer mappings yet</Typography>
          <Typography variant="body-medium" color={theme.palette.lightBg.low}>
            Go back to Step 1 and map at least one NinjaOne customer to a
            DeskDay customer to begin device mapping.
          </Typography>
        </Stack>
      ) : (
        <Stack
          direction={"row"}
          flex={1}
          sx={{
            width: "100%",
            overflow: "clip",
          }}
          divider={<Divider orientation="vertical" flexItem />}
        >
          <Box
            sx={{
              display: "block",
              overflowY: "scroll",
              height: "100%",
              width: "350px",
            }}
          >
            <Tabs
              orientation="vertical"
              value={selectedCustomer}
              onChange={(_, index) => {
                setSelectedCustomer(index);
              }}
            >
              {(ninjaMappedCustomers as NinjaMappedCustomer[] | undefined)?.map(
                (customer) => (
                  <Tab
                    key={customer.mapping_id}
                    label={customer.external_customer_name}
                  />
                ),
              )}
            </Tabs>
          </Box>
          <Box
            sx={{
              display: "block",
              overflowY: "scroll",
              height: "100%",
              flex: "1",
              padding: "32px 32px 0px 32px",
              position: "relative",
            }}
          >
            {selectedOrgId &&
            !isNinjaDeviceLoading &&
            !isNinjaDeviceFetching &&
            !isBuildingRows &&
            currentDevices.length === 0 ? (
              <Stack
                alignItems="center"
                justifyContent="center"
                height="100%"
                spacing={1}
                sx={{ textAlign: "center" }}
              >
                <Typography
                  variant="body-medium"
                  color={theme.palette.lightBg.low}
                >
                  No devices found for this customer in NinjaOne RMM.
                </Typography>
              </Stack>
            ) : (
              <DataTable
                isDataLoading={
                  isNinjaDeviceLoading ||
                  (isNinjaDeviceFetching && !isFetchingNinjaDevicesNextPage) ||
                  isBuildingRows
                }
                headers={header}
                data={currentDevices}
                uniqueKey="device_id"
                cellBuilders={cellBuilder}
                selectedItems={[]}
                currentPageNumber={0}
                itemsPerPage={DEVICE_PAGE_SIZE}
                multiselect={false}
                containerStyles={{ height: "100%", paddingBottom: "58px" }}
                headerCellStyles={{
                  textTransform: "none",
                }}
              />
            )}
            {hasNinjaDevicesNextPage && selectedOrgId && (
              <Stack
                justifyContent={"center"}
                alignItems={"center"}
                position="absolute"
                bottom="8px"
                left={"50%"}
                sx={{
                  transform: "translateX(-50%)",
                  zIndex: 1000,
                }}
              >
                <Button
                  variant="ghost"
                  onClick={() => fetchNinjaDevicesNextPage()}
                  disabled={isFetchingNinjaDevicesNextPage}
                  loading={isFetchingNinjaDevicesNextPage}
                  sx={{
                    textTransform: "none",
                    color: theme.palette.primary.main,
                    "&:hover": {
                      backgroundColor: "transparent",
                      textDecoration: "underline",
                    },
                  }}
                >
                  {isFetchingNinjaDevicesNextPage
                    ? "Loading..."
                    : "Load More Devices"}
                </Button>
              </Stack>
            )}
          </Box>
        </Stack>
      )}
    </Stack>
  );
});

const header = [
  {
    name: "NinjaOne RMM Device",
    sortable: false,
    key: "device_name",
  },
  {
    name: "DeskDay User",
    sortable: true,
    key: "user_name",
  },
];
